<template>
  <div class="login-container">
    <div class="login-box">
      <h1><BrandLogo large /></h1>
      <button type="button" class="lang-switch" @click="setLang(lang === 'tr' ? 'en' : 'tr')">{{ lang === 'tr' ? 'English' : 'Türkçe' }}</button>
      <p class="subtitle">{{ $t('Geotechnical Analysis Suite') }}</p>

      <form @submit.prevent="handleSubmit">
        <div class="tabs">
          <button
            v-for="tab in ['login', 'signup']"
            :key="tab"
            type="button"
            class="tab-btn"
            :class="{ active: activeTab === tab }"
            @click="activeTab = tab"
          >
            {{ tab === 'login' ? $t('Login') : $t('Sign Up') }}
          </button>
        </div>

        <div class="form-group">
          <label>{{ $t('Email') }}</label>
          <input
            v-model="email"
            type="email"
            placeholder="your@email.com"
            required
          >
        </div>

        <div class="form-group">
          <label>{{ $t('Password') }}</label>
          <input
            v-model="password"
            type="password"
            placeholder="••••••••"
            required
            minlength="8"
          >
        </div>

        <button type="submit" class="btn btn-primary" :disabled="loading">
          {{ loading ? $t('Loading...') : activeTab === 'login' ? $t('Login') : $t('Create Account') }}
        </button>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <p v-if="activeTab === 'login'" class="forgot">
          <router-link to="/forgot">{{ $t('Forgot your password?') }}</router-link>
        </p>
      </form>

      <p class="info-text">
        <strong>{{ $t('Free') }} {{ $t('tier:') }}</strong> {{ $t('Basic programs, saving, reports, export') }}<br>
        <strong>Pro {{ $t('tier:') }}</strong> {{ $t('More programs: Plus, Pro, Pro Plus') }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { lang, setLang, t } from '../i18n'
import BrandLogo from '../components/BrandLogo.vue'

const router = useRouter()
const authStore = useAuthStore()

const email = ref('')
const password = ref('')
const activeTab = ref('login')
const loading = ref(false)
const error = ref('')

const handleSubmit = async () => {
  loading.value = true
  error.value = ''

  try {
    if (activeTab.value === 'login') {
      await authStore.login(email.value, password.value)
    } else {
      await authStore.signup(email.value, password.value)
    }
    router.push('/')
  } catch (err) {
    error.value = t(authStore.error || 'Something went wrong')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.lang-switch {
  position: absolute;
  top: 14px;
  right: 16px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 14px;
  padding: 3px 10px;
  font-size: 12px;
  cursor: pointer;
  color: inherit;
}

.forgot {
  margin: 14px 0 0 0;
  font-size: 14px;
  text-align: center;
}

.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-box {
  width: 100%;
  max-width: 400px;
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dark .login-box {
  background: #2a2a2a;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

h1 {
  font-size: 28px;
  margin-bottom: 8px;
  text-align: center;
}

.subtitle {
  text-align: center;
  color: #666;
  margin-bottom: 30px;
  font-size: 14px;
}

.dark .subtitle {
  color: #aaa;
}

.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 30px;
}

.tab-btn {
  flex: 1;
  padding: 10px;
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #666;
  transition: all 0.2s;
}

.dark .tab-btn {
  background: #3a3a3a;
  color: #aaa;
}

.tab-btn.active {
  background: #333;
  color: white;
}

.dark .tab-btn.active {
  background: #4CAF50;
  color: white;
}

.form-group {
  margin-bottom: 20px;
}

label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
}

input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.2s;
}

.dark input {
  background: #3a3a3a;
  border-color: #555;
  color: #fff;
}

input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
}

.btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #4CAF50;
  color: white;
}

.btn:hover:not(:disabled) {
  opacity: 0.9;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  margin-top: 16px;
  padding: 12px;
  background: #ffebee;
  color: #c62828;
  border-radius: 4px;
  font-size: 14px;
}

.dark .error-message {
  background: #3a2a2a;
  color: #ff9999;
}

.info-text {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid #eee;
  font-size: 13px;
  color: #666;
  line-height: 1.6;
}

.dark .info-text {
  border-top-color: #444;
  color: #aaa;
}

.info-text strong {
  display: block;
  margin-bottom: 4px;
  color: #333;
}

.dark .info-text strong {
  color: #fff;
}
</style>
